import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  createFilterOptions,
  TextField,
  Typography,
} from "@mui/material";
import {
  normalizePeopleSelection,
  normalizePeopleSelectionId,
} from "./peopleSelection";
import type { ReactElement, ReactNode } from "react";

export interface PeopleOption {
  id: string;
  displayName: string;
  /** Caption rendered under the name. */
  subtitle?: string;
}

interface SelectablePeopleOption extends PeopleOption {
  unavailable?: boolean;
}

const filterPeopleOptions = createFilterOptions<SelectablePeopleOption>();

interface PeopleMultiSelectProps {
  label: ReactNode;
  options: PeopleOption[];
  selectedIds: string[];
  onChange?: (selectedIds: string[]) => void;
  renderAvatar?: (person: PeopleOption) => ReactElement;
  readOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  /** Names used to label selected people who are not selectable options (e.g. no longer on the account team). */
  fallbackOptions?: PeopleOption[];
}

function normalizeOptions(options: PeopleOption[]): SelectablePeopleOption[] {
  return options.reduce<SelectablePeopleOption[]>((uniqueOptions, option) => {
    const normalizedId = normalizePeopleSelectionId(option.id);
    if (
      !normalizedId ||
      uniqueOptions.some((existingOption) => existingOption.id === normalizedId)
    ) {
      return uniqueOptions;
    }

    return [...uniqueOptions, { ...option, id: normalizedId }];
  }, []);
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function PersonAvatar({
  person,
  renderAvatar,
}: Readonly<{
  person: PeopleOption;
  renderAvatar?: (person: PeopleOption) => ReactElement;
}>) {
  return (
    renderAvatar?.(person) ?? (
      <Avatar sx={{ width: 24, height: 24, fontSize: "0.75rem" }}>
        {getInitials(person.displayName)}
      </Avatar>
    )
  );
}

function SelectedPeopleChips({
  people,
  renderAvatar,
}: Readonly<{
  people: PeopleOption[];
  renderAvatar?: (person: PeopleOption) => ReactElement;
}>) {
  if (people.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        --
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {people.map((person) => (
        <Chip
          key={person.id}
          avatar={<PersonAvatar person={person} renderAvatar={renderAvatar} />}
          label={person.displayName}
          size="small"
        />
      ))}
    </Box>
  );
}

export function PeopleMultiSelect({
  label,
  options,
  selectedIds,
  onChange,
  renderAvatar,
  readOnly,
  disabled,
  loading,
  placeholder,
  fallbackOptions,
}: Readonly<PeopleMultiSelectProps>) {
  const normalizedOptions = normalizeOptions(options);
  const optionsById = new Map<string, SelectablePeopleOption>(
    normalizedOptions.map((option) => [option.id, option]),
  );
  const fallbackOptionsById = new Map<string, SelectablePeopleOption>(
    normalizeOptions(fallbackOptions ?? []).map((option) => [
      option.id,
      option,
    ]),
  );
  const selectedPeople: SelectablePeopleOption[] = normalizePeopleSelection(
    selectedIds,
  ).map((id) => {
    const option = optionsById.get(id);
    if (option) return option;

    return {
      id,
      displayName: fallbackOptionsById.get(id)?.displayName || "N/A",
      unavailable: true,
    };
  });
  const autocompleteOptions = [
    ...normalizedOptions,
    ...selectedPeople.filter((person) => person.unavailable),
  ];

  if (readOnly) {
    return (
      <Box>
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
        <SelectedPeopleChips
          people={selectedPeople}
          renderAvatar={renderAvatar}
        />
      </Box>
    );
  }

  return (
    <Autocomplete
      multiple
      options={autocompleteOptions}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(people, state) =>
        filterPeopleOptions(people, state).filter(
          (person) => !person.unavailable,
        )
      }
      value={selectedPeople}
      loading={loading}
      disabled={disabled}
      onChange={(_, newValue) => {
        onChange?.(
          normalizePeopleSelection(newValue.map((option) => option.id)),
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            avatar={
              <PersonAvatar person={option} renderAvatar={renderAvatar} />
            }
            label={option.displayName}
            size="small"
          />
        ))
      }
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as { key: string } & Record<
          string,
          unknown
        >;
        return (
          <Box component="li" key={key} {...otherProps}>
            <Box sx={{ mr: 1.5 }}>
              <PersonAvatar person={option} renderAvatar={renderAvatar} />
            </Box>
            <Box>
              <Typography variant="body2">{option.displayName}</Typography>
              {option.subtitle && (
                <Typography variant="caption" color="textSecondary">
                  {option.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
        />
      )}
    />
  );
}
